import MultiSelectInput from '@repo/ui/multiselect-input'
import SelectInput from '@repo/ui/select-input'
import { useQuery } from '@tanstack/react-query'

import { getPeriods } from '../../../../api/periods/get-periods'
import { getProducts } from '../../../../api/products/get-products'
import useTranslate from '../../../../lib/hooks/use-translate'
import {
  HostFamilyPriceTypeEnum,
  hostFamilyPriceTypeEnumMapping
} from '../../../../lib/types/enums/ol/host-family-price-type.enum'
import { OLHostFamilyPricePerNightFilters } from '../../../../lib/types/models/ol/host-family-price-per-night/ol-host-family-price-per-night'
import { t } from '../../../../locale/utils'
import { getLocalizedLabel } from '../../../../utils/functions/get-localized-label'
import { yearsFilterOptions } from '../../../../views/pcr-personal-space/utils'

interface Props {
  filter?: OLHostFamilyPricePerNightFilters
  onFilterChange: (filter: OLHostFamilyPricePerNightFilters) => void
}

export function OlHostFamilyPricesFilter({ filter, onFilterChange }: Props) {
  const { currentLanguage } = useTranslate()
  function setFilter<K extends keyof OLHostFamilyPricePerNightFilters>(
    key: K,
    value: OLHostFamilyPricePerNightFilters[K]
  ) {
    onFilterChange({ ...filter, [key]: value })
  }

  const { data: periods } = useQuery({
    queryKey: ['periods'],
    queryFn: () => getPeriods({ version: 'BASIC' })
  })

  const { data: products } = useQuery({
    queryKey: ['products'],
    queryFn: () => getProducts()
  })

  return (
    <div className={'flex flex-col gap-3'}>
      <div />

      <SelectInput
        label={t('commissions.filters.year')}
        options={yearsFilterOptions}
        selectedOption={
          filter?.year ? { label: filter.year.toString(), value: filter.year } : undefined
        }
        onChange={(year) => setFilter('year', year)}
      />

      <MultiSelectInput
        label={t('commissions.filters.products')}
        variant={'primary'}
        options={products?.data.map((product) => ({
          label: product.name,
          value: product.name
        }))}
        selectedOptions={filter?.products?.map((product) => ({
          label: product,
          value: product
        }))}
        onChange={(products) => setFilter('products', products)}
      />

      <MultiSelectInput
        label={t('commissions.filters.periods')}
        variant={'primary'}
        options={periods?.data.map((period) => ({
          label: getLocalizedLabel(period, currentLanguage, {
            fr: 'name',
            en: 'nameEn'
          }),
          value: period.name
        }))}
        selectedOptions={filter?.periods?.map((period) => {
          const selectedPeriod = periods?.data.find((p) => p.name === period)
          if (!selectedPeriod) return { label: period, value: period }
          return {
            label: getLocalizedLabel(selectedPeriod, currentLanguage, {
              fr: 'name',
              en: 'nameEn'
            }),
            value: period
          }
        })}
        onChange={(periods) => setFilter('periods', periods)}
      />

      <SelectInput
        label={t('commissions.appliedToHost.header')}
        options={Object.values(HostFamilyPriceTypeEnum).map((value) => ({
          label: t(hostFamilyPriceTypeEnumMapping[value].label),
          value: value
        }))}
        selectedOption={
          filter?.appliedToHost && {
            label: t(hostFamilyPriceTypeEnumMapping[filter.appliedToHost].label),
            value: filter.appliedToHost
          }
        }
        onChange={(appliedToHost) => setFilter('appliedToHost', appliedToHost)}
      />
    </div>
  )
}
